// Libs
import classNames from 'classnames/bind';
import ReactQuill from 'react-quill';
import { Button, Flex, IconPaperclipLine, IconXLine, Text } from '@instructure/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createEntry, createReply, updateEntry } from '~/thunks/discussion/discussionThunk';
import { IDiscussionEntry } from '~/utils/interface/discussion';
// Styles, images, icons
import styles from './ReplyInput.module.scss';
import 'react-quill/dist/quill.snow.css';
import './ReplyInput.scss';

type Props = {
  showCancelButton?: boolean;
  courseId?: string;
  discussionId?: string;
  entryId?: string;
  data?: IDiscussionEntry;
  onCancel?: () => void;
};

const cx = classNames.bind(styles);

const ReplyInput = (props: Props) => {
  //#region Destructuring Props
  const { showCancelButton = false, courseId, discussionId, entryId, data, onCancel } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/discussion', 'common/base']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [value, setValue] = useState<string>(data?.message ?? EMPTY_STRING);
  const [file, setFile] = useState<File>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeletedFile, setIsDeletedFile] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCreateEntry = () => {
    if (!courseId || !discussionId) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('message', value);
    formData.append('courseId', courseId);
    formData.append('discussionId', discussionId);
    if (file) {
      formData.append('file', file);
    }
    dispatch(createEntry(formData))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreateReply = () => {
    if (!courseId || !discussionId || !entryId) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('message', value);
    formData.append('courseId', courseId);
    formData.append('discussionId', discussionId);
    formData.append('entryId', entryId);
    if (file) {
      formData.append('file', file);
    }
    dispatch(createReply(formData))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateEntry = () => {
    if (!courseId || !discussionId || !entryId) return;

    setIsLoading(true);
    dispatch(
      updateEntry({
        courseId: courseId,
        discussionId: discussionId,
        entryId: String(entryId),
        message: value,
        attachment: !isDeletedFile && (data?.originAttachments?.length) ? data?.originAttachments?.[0] : undefined,
      })
    )
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = () => {
    if (!entryId) {
      handleCreateEntry();
      return;
    }

    if (data) {
      handleUpdateEntry();
      return;
    }

    handleCreateReply();
  };

  const handleClear = () => {
    setValue('');
    setFile(undefined);
    setIsDeletedFile(false);
  };

  const handleCancel = () => {
    onCancel && onCancel();
    handleClear();
  };

  const handleSelectAttachment = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    file && setFile(file);
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      <ReactQuill value={value} theme='snow' onChange={setValue} />
      <Flex margin='small 0 0 0' justifyItems='space-between'>
        {!data && (
          <>
            {file ? (
              <div className={cx('attachButton')}>
                <IconPaperclipLine />
                <Text size='contentSmall'>{file.name}</Text>
                <IconXLine color='error' onClick={() => setFile(undefined)} />
              </div>
            ) : (
              <div className={cx('attachButton')} onClick={handleSelectAttachment}>
                <IconPaperclipLine />
                <Text size='contentSmall'>{t('discussion_attach_button')}</Text>
                <input type='file' ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
              </div>
            )}
          </>
        )}

        {data?.attachments?.[0]?.displayName && !isDeletedFile ? (
          <div className={cx('attachButton')}>
            <IconPaperclipLine />
            <Text size='contentSmall'>{data.attachments?.[0]?.displayName}</Text>
            <IconXLine color='error' onClick={() => setIsDeletedFile(true)} />
          </div>
        ) : (
          <div></div>
        )}

        <div>
          {showCancelButton && (
            <Button size='small' color='secondary' onClick={handleCancel}>
              {t('cancel_button_label', { ns: 'common/base' })}
            </Button>
          )}

          <Button
            disabled={value.length === 0 || isLoading}
            size='small'
            color='primary'
            margin='0 0 0 small'
            onClick={handleSubmit}
          >
            {isLoading ? t('saving_button_label', { ns: 'common/base' }) : t('discussion_post_reply_button')}
          </Button>
        </div>
      </Flex>
    </div>
  );
};

export default ReplyInput;
